/**
 * Operating Profile Helpers
 *
 * Centralized utilities for operating profile-based feature gating throughout PCNGuard Fleet.
 *
 * Usage:
 * ```tsx
 * import { BusinessModelHelpers } from '../utils/businessModel';
 * import { useBranding } from '../context/BrandingContext';
 *
 * const MyComponent = () => {
 *   const { organization } = useBranding();
 *   const canTransfer = BusinessModelHelpers.canTransferLiability(organization);
 *
 *   return canTransfer ? <TransferButton /> : <PayButton />;
 * };
 * ```
 */

import { BusinessModel, Organization } from '../types';
import { resolveModuleAccess } from '../services/moduleAccess';

export const BusinessModelHelpers = {
  /**
   * Check if organization uses hire workflows (drivers, hires, liability transfers)
   * Returns false for fleet operators who manage their own vehicles
   */
  usesHireWorkflows(org: Organization | null): boolean {
    if (!org) return false;
    return resolveModuleAccess({ organization: org, role: 'admin' }).canAccessModule('hires');
  },

  /**
   * Check if organization can prepare transfer evidence packs for hirers
   * Returns false for fleet operators
   * Returns true for hire firms (with warnings for unverified startups)
   */
  canTransferLiability(org: Organization | null): boolean {
    if (!org) return false;

    // Check runtime toggle first
    if (!org.enable_transfer_features) return false;

    // Only hire firms can transfer
    return (
      org.business_model === 'hire_firm_registered' ||
      org.business_model === 'hire_firm_startup'
    );
  },

  /**
   * Check if organization is a fleet operator
   * Fleet operators manage their own vehicles and don't hire them out
   */
  isFleetOperator(org: Organization | null): boolean {
    return org?.business_model === 'fleet_operator';
  },

  /**
   * Check if organization is a PCO hire operator with company details confirmed.
   * Transfer readiness itself remains PCN-and-evidence-level.
   */
  isVerifiedHireFirm(org: Organization | null): boolean {
    return (
      org?.business_model === 'hire_firm_registered' &&
      org?.legal_entity_verified === true
    );
  },

  /**
   * Check if organization is an older setup-incomplete PCO hire profile.
   */
  isStartupHireFirm(org: Organization | null): boolean {
    return org?.business_model === 'hire_firm_startup';
  },

  /**
   * Check if organization needs company setup details completed.
   * Transfer readiness is still handled by hire agreement evidence checks.
   */
  needsLegalVerification(org: Organization | null): boolean {
    if (!org) return false;

    // Fleet operators don't need verification
    if (org.business_model === 'fleet_operator') return false;

    // Hire firms need verification if not already verified
    return !org.legal_entity_verified;
  },

  /**
   * Get human-readable label for business model
   */
  getBusinessModelLabel(model: BusinessModel): string {
    const labels: Record<BusinessModel, string> = {
      hire_firm_registered: 'PCO Hire Operator',
      hire_firm_startup: 'PCO Hire Operator (setup incomplete)',
      fleet_operator: 'Owned / Managed Fleet'
    };
    return labels[model];
  },

  /**
   * Get description for business model
   */
  getBusinessModelDescription(model: BusinessModel): string {
    const descriptions: Record<BusinessModel, string> = {
      hire_firm_registered:
        'Match PCNs to hirers, check hire-agreement evidence, prepare transfer packs, and track recovery.',
      hire_firm_startup:
        'Use the PCO hire workflow while you complete company and agreement setup details.',
      fleet_operator:
        'Manage company vehicles used by staff or contractors. Pay, appeal, or assign internally.'
    };
    return descriptions[model];
  },

  /**
   * Get color theme for business model (for UI styling)
   */
  getBusinessModelColor(model: BusinessModel): {
    primary: string;
    background: string;
    border: string;
  } {
    const colors: Record<
      BusinessModel,
      { primary: string; background: string; border: string }
    > = {
      hire_firm_registered: {
        primary: 'text-primary-600 dark:text-primary-400',
        background: 'bg-primary-50 dark:bg-primary-900/20',
        border: 'border-primary-500'
      },
      hire_firm_startup: {
        primary: 'text-amber-600 dark:text-amber-400',
        background: 'bg-amber-50 dark:bg-amber-900/20',
        border: 'border-amber-500'
      },
      fleet_operator: {
        primary: 'text-blue-600 dark:text-blue-400',
        background: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-500'
      }
    };
    return colors[model];
  },

  /**
   * Get icon name for business model (lucide-react icon names)
   */
  getBusinessModelIcon(model: BusinessModel): string {
    const icons: Record<BusinessModel, string> = {
      hire_firm_registered: 'ShieldCheck',
      hire_firm_startup: 'AlertTriangle',
      fleet_operator: 'Car'
    };
    return icons[model];
  },

  /**
   * Get verification status label
   */
  getVerificationStatusLabel(
    status: Organization['legal_entity_verification_status']
  ): string {
    const labels: Record<
      Organization['legal_entity_verification_status'],
      string
    > = {
      verified: 'Verified',
      pending: 'Verification Pending',
      unverified: 'Not Verified',
      not_applicable: 'N/A'
    };
    return labels[status];
  },

  /**
   * Get verification status color (for badges)
   */
  getVerificationStatusColor(
    status: Organization['legal_entity_verification_status']
  ): string {
    const colors: Record<
      Organization['legal_entity_verification_status'],
      string
    > = {
      verified: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
      unverified: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
      not_applicable: 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200'
    };
    return colors[status];
  },

  /**
   * Check if organization should see transfer liability warnings
   * Applies to startup hire firms and unverified registered hire firms
   */
  shouldShowTransferWarnings(org: Organization | null): boolean {
    if (!org) return false;

    // No warnings for fleet operators (they can't transfer anyway)
    if (org.business_model === 'fleet_operator') return false;

    // Show warnings if hire firm is not verified
    return !org.legal_entity_verified;
  },

  /**
   * Get transfer liability warning message
   */
  getTransferWarningMessage(org: Organization | null): string | null {
    if (!this.shouldShowTransferWarnings(org)) return null;

    if (org?.business_model === 'hire_firm_startup') {
      return 'Complete your PCO setup details and verify your hire agreement wording before preparing transfer evidence packs.';
    }

    if (org?.business_model === 'hire_firm_registered' && !org.legal_entity_verified) {
      return 'Company details are incomplete. Transfer pack readiness still depends on the specific PCN, hire dates, signed agreement, hirer details, and liability wording.';
    }

    return null;
  }
};
